import { Suspense, lazy, useEffect } from "react";
import { lazyWithReload } from "@/lib/lazyWithReload";
// Customer App - Lovable Cloud
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

function RideRedirect() {
  const { bookingId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/customer/booking/${bookingId}${search || ""}`} replace />;
}
import { CustomerAuthProvider } from "@/hooks/useCustomerAuth";
import { ThemeModeProvider } from "@/hooks/useThemeMode";
import AppVersionGate from "@/components/AppVersionGate";
import { useCustomerPushRegistration } from "@/hooks/useCustomerPushRegistration";
import { usePushNotificationHandler } from "@/hooks/usePushNotificationHandler";
import { useGlobalScratchCardCheck } from "@/hooks/useGlobalScratchCardCheck";
import { SteeringLoader, FullScreenOverlayLoader } from '@/components/ui/steering-loader';


function CustomerPushBridge() {
  useCustomerPushRegistration();
  usePushNotificationHandler();
  useGlobalScratchCardCheck();
  return null;
}

// Prefetch the heaviest customer routes during idle so navigation feels
// instant after the first screen has painted.
function RoutePrefetcher() {
  useEffect(() => {
    const w = window as any;
    const schedule = (cb: () => void) =>
      typeof w.requestIdleCallback === "function"
        ? w.requestIdleCallback(cb, { timeout: 4000 })
        : setTimeout(cb, 2000);
    schedule(() => {
      import("./pages/customer/CustomerHome");
      import("./pages/customer/CustomerBookRide");
      import("./pages/customer/CustomerBookings");
      import("./pages/customer/CustomerProfile");
    });
  }, []);
  return null;
}

const CustomerAuthGuard = lazyWithReload(() => import("./components/customer/CustomerAuthGuard"));
const CustomerLocationGate = lazyWithReload(() => import("./components/customer/CustomerLocationGate"));

// Customer App Pages
const CustomerLogin = lazyWithReload(() => import("./pages/customer/CustomerLogin"));
const CustomerHome = lazyWithReload(() => import("./pages/customer/CustomerHome"));
const CustomerBookings = lazyWithReload(() => import("./pages/customer/CustomerBookings"));
const CustomerBookingDetail = lazyWithReload(() => import("./pages/customer/CustomerBookingDetail"));
const CustomerProfile = lazyWithReload(() => import("./pages/customer/CustomerProfile"));
const CustomerBookRide = lazyWithReload(() => import("./pages/customer/CustomerBookRide"));
const CustomerSearchingDriver = lazyWithReload(() => import("./pages/customer/CustomerSearchingDriver"));
const CustomerSavedAddresses = lazyWithReload(() => import("./pages/customer/CustomerSavedAddresses"));
const CustomerNotifications = lazyWithReload(() => import("./pages/customer/CustomerNotifications"));
const CustomerTerms = lazyWithReload(() => import("./pages/customer/CustomerTerms"));
const CustomerPrivacyPolicy = lazyWithReload(() => import("./pages/customer/CustomerPrivacyPolicy"));
const CustomerAbout = lazyWithReload(() => import("./pages/customer/CustomerAbout"));
const CustomerHelpSupport = lazyWithReload(() => import("./pages/customer/CustomerHelpSupport"));
const CustomerSettings = lazyWithReload(() => import("./pages/customer/CustomerSettings"));
const CustomerEditProfile = lazyWithReload(() => import("./pages/customer/CustomerEditProfile"));

const CustomerDeleteAccount = lazyWithReload(() => import("./pages/customer/CustomerDeleteAccount"));
const NotFound = lazyWithReload(() => import("./pages/NotFound"));

const CustomerMarketingDetail = lazyWithReload(() => import("./pages/customer/CustomerMarketingDetail"));

const queryClient = new QueryClient();

function PageLoader() {
  return <FullScreenOverlayLoader />;
}

function AppRoutes() {
  const location = useLocation();
  return (
    <main className="min-h-screen">
      <Suspense fallback={<PageLoader />}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Navigate to="/customer/login" replace />} />
            <Route path="/customer/login" element={<CustomerLocationGate><CustomerLogin /></CustomerLocationGate>} />
            <Route path="/customer/home" element={<CustomerLocationGate><CustomerAuthGuard><CustomerHome /></CustomerAuthGuard></CustomerLocationGate>} />
            <Route path="/customer/bookings" element={<CustomerLocationGate><CustomerAuthGuard><CustomerBookings /></CustomerAuthGuard></CustomerLocationGate>} />
            <Route path="/customer/booking/:id" element={<CustomerLocationGate><CustomerAuthGuard><CustomerBookingDetail /></CustomerAuthGuard></CustomerLocationGate>} />
            {/* Deep-link alias used by scratch-card notifications: /ride/:bookingId?openScratch=1 */}
            <Route path="/ride/:bookingId" element={<RideRedirect />} />
            <Route path="/customer/profile" element={<CustomerLocationGate><CustomerAuthGuard><CustomerProfile /></CustomerAuthGuard></CustomerLocationGate>} />
            <Route path="/customer/book" element={<CustomerLocationGate><CustomerAuthGuard><CustomerBookRide /></CustomerAuthGuard></CustomerLocationGate>} />
            <Route path="/customer/searching/:requestId" element={<CustomerLocationGate><CustomerAuthGuard><CustomerSearchingDriver /></CustomerAuthGuard></CustomerLocationGate>} />
            <Route path="/customer/saved-places" element={<CustomerLocationGate><CustomerAuthGuard><CustomerSavedAddresses /></CustomerAuthGuard></CustomerLocationGate>} />
            <Route path="/customer/notifications" element={<CustomerLocationGate><CustomerAuthGuard><CustomerNotifications /></CustomerAuthGuard></CustomerLocationGate>} />
            <Route path="/customer/about" element={<CustomerLocationGate><CustomerAuthGuard><CustomerAbout /></CustomerAuthGuard></CustomerLocationGate>} />
            <Route path="/customer/help-support" element={<CustomerLocationGate><CustomerAuthGuard><CustomerHelpSupport /></CustomerAuthGuard></CustomerLocationGate>} />
            <Route path="/customer/settings" element={<CustomerLocationGate><CustomerAuthGuard><CustomerSettings /></CustomerAuthGuard></CustomerLocationGate>} />
            <Route path="/customer/edit-profile" element={<CustomerLocationGate><CustomerAuthGuard><CustomerEditProfile /></CustomerAuthGuard></CustomerLocationGate>} />


            <Route path="/customer/terms" element={<CustomerTerms />} />
            <Route path="/terms" element={<CustomerTerms />} />
            <Route path="/customer/privacy" element={<CustomerPrivacyPolicy />} />
            <Route path="/privacy" element={<CustomerPrivacyPolicy />} />
            <Route path="/customer/delete-account" element={<CustomerDeleteAccount />} />
            <Route path="/customer/marketing/:id" element={<CustomerLocationGate><CustomerAuthGuard><CustomerMarketingDetail /></CustomerAuthGuard></CustomerLocationGate>} />
            <Route path="/delete-account" element={<CustomerDeleteAccount />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AnimatePresence>
      </Suspense>
    </main>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeModeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <CustomerAuthProvider>
            <AppVersionGate>
              <CustomerPushBridge />
              <RoutePrefetcher />
              <AppRoutes />
            </AppVersionGate>
          </CustomerAuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeModeProvider>
  </QueryClientProvider>
);

export default App;
